import React, {
  ForwardedRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { FlatList, Platform, ViewToken } from "react-native";
import { graphql, useFragment } from "react-relay";
import { WidgetTeamScheduleListItem } from "./WidgetTeamScheduleListItem";
import { useWidgetLayout } from "../WidgetLayoutContext";
import { useWidgetLayoutContentSize } from "../useWidgetLayoutContentSize";
import { WidgetTeamScheduleContentBaseProps } from "./WidgetTeamScheduleContentBaseProps";
import { DateTime } from "luxon";
import { Box } from "arkh";
import { useWidgetTeamScheduleSub } from "./useWidgetTeamScheduleSubscription";
import { WidgetTeamScheduleContentList_teamSchedule$key } from "./__generated__/WidgetTeamScheduleContentList_teamSchedule.graphql";
import { BaseWidgetProps } from "../BaseProps";

export const conf = {
  itemVisiblePercentThreshold: 50,
  minimumViewTime: 500,
};

export type WidgetTeamScheduleContentListRef = {
  scrollToItem: (itemId: string | null) => void;
};
export const WidgetTeamScheduleContentList = React.memo(
  React.forwardRef(_WidgetTeamScheduleContentList)
);

function _WidgetTeamScheduleContentList(
  props: {
    teamSchedule: WidgetTeamScheduleContentList_teamSchedule$key;
    scrollIndex?: number | undefined;
    nowIndexes?: number[] | undefined;
    now: DateTime;
    teamId: string;
    onPressRoutineOccasion: (routineId: string, sequenceNumber: number) => void;
    mode: BaseWidgetProps["mode"] | undefined;

    size: WidgetTeamScheduleContentBaseProps["size"];
    tint: string;
    isDarkTint: boolean;
    interactive?: boolean;
    isFocused?: boolean;
    idiom: BaseWidgetProps["idiom"];
    onScroll?: (id: string) => void;
  },
  ref: ForwardedRef<WidgetTeamScheduleContentListRef>
) {
  const { width } = useWidgetLayout();

  const teamSchedule = useFragment(
    graphql`
      fragment WidgetTeamScheduleContentList_teamSchedule on TeamSchedule {
        id
        label
        freq
        events(includeInlineRoutines: true) {
          id
          startAt
          ... on RoutineOccasion {
            routine {
              id
              description
              descriptionImages {
                id
              }
            }
          }
          ... on TeamScheduleInlineRoutineOccasion {
            taskExecution {
              routine {
                id
                description
                descriptionImages {
                  id
                }
              }
            }
          }

          ...useWidgetTeamScheduleSubscription_TeamScheduleEventOccasion
          ...WidgetTeamScheduleListItem_item
        }
      }
    `,
    props.teamSchedule
  );

  const listData = useMemo(() => {
    return teamSchedule.events;
  }, [teamSchedule.events]);

  const unit = useWidgetLayoutContentSize().unit / 3.05;

  const height = Math.floor(unit);

  const nowHeight =
    height *
    (props.size === "TWO_X_THREE" || props.size === "TWO_X_FOUR"
      ? 3
      : props.size === "TWO_X_ONE"
      ? 1
      : 2);

  const renderItem = useCallback(
    ({ item, index }: { item: (typeof listData)[number]; index: number }) => {
      const status = props.nowIndexes?.includes(index)
        ? "active"
        : props.nowIndexes?.includes(index - 1) &&
          DateTime.fromISO(item.startAt) < props.now.plus({ hour: 1 })
        ? "soon"
        : "none";

      const calcHeight = (): number => {
        if (status !== "active") {
          return height;
        }
        const routine = item.routine ?? item.taskExecution?.routine;
        if (
          routine &&
          ((routine.description && routine.description.trim().length > 0) ||
            routine.descriptionImages.length > 0)
        ) {
          return nowHeight;
        }

        if (!routine) {
          return height;
        }
        return height;
      };

      return (
        <WidgetTeamScheduleListItem
          key={item.id}
          height={calcHeight()}
          status={status}
          tint={props.tint}
          width={width}
          item={item}
          size={props.size}
          isLast={listData.length - 1 === index}
          isDarkTint={props.isDarkTint}
          onPressRoutineOccasion={props.onPressRoutineOccasion}
          freq={teamSchedule.freq}
        />
      );
    },
    [
      height,
      listData.length,
      nowHeight,
      props.isDarkTint,
      props.now,
      props.nowIndexes,
      props.onPressRoutineOccasion,
      props.size,
      props.tint,
      teamSchedule.freq,
      width,
    ]
  );

  const lengths = useMemo(
    () =>
      listData.map((_, index) =>
        props.nowIndexes?.includes(index) ? nowHeight : height
      ),
    [height, listData, nowHeight, props.nowIndexes]
  );

  const getItemLayout = useCallback(
    (_data: unknown, index: number) => ({
      index,
      length: lengths[index] ?? 0,
      offset: lengths
        .slice(0, Math.max(index - 1, 0))
        .reduce((acc, sum) => acc + sum, 0),
    }),
    [lengths]
  );

  const listRef = useRef<FlatList<(typeof listData)[number]> | null>(null);
  const scrollToNow = useCallback(() => {
    if (
      props.scrollIndex !== undefined &&
      props.interactive !== false &&
      listData.length > 0
    ) {
      listRef.current?.scrollToOffset({
        offset: getItemLayout(
          null,
          props.size === "TWO_X_ONE" ? props.scrollIndex + 1 : props.scrollIndex
        ).offset,
      });
    }
  }, [
    props.scrollIndex,
    props.interactive,
    props.size,
    listData.length,
    getItemLayout,
  ]);

  useEffect(() => {
    if (
      props.scrollIndex !== undefined &&
      props.interactive !== false &&
      !props.isFocused
    ) {
      scrollToNow();
    }
  }, [
    props.nowIndexes,
    scrollToNow,
    props.interactive,
    props.scrollIndex,
    props.isFocused,
    props.idiom,
  ]);

  useImperativeHandle(ref, () => ({
    scrollToItem: (itemId: string | null) => {
      if (!itemId) {
        listRef.current?.scrollToOffset({
          offset: 0,
          animated: true,
        });
        return;
      }

      const index = (teamSchedule?.events ?? []).findIndex(
        (item) => item.id === itemId
      );
      if (index !== -1) {
        listRef.current?.scrollToIndex({
          index,
          animated: true,
        });
      }
    },
  }));

  useWidgetTeamScheduleSub(
    teamSchedule?.events ?? [],
    props.teamId,
    props.interactive === false
  );

  const [viewableItems, setViewableItems] = useState<ViewToken[] | null>(null);
  const firstItem = useMemo(
    () =>
      viewableItems && props.isFocused && Platform.OS !== "web"
        ? viewableItems.find(({ item }) => item?.id)?.item?.id
        : null,
    [props.isFocused, viewableItems]
  );

  const { isFocused, onScroll: setScroll } = props;

  useEffect(() => {
    if (isFocused && firstItem && setScroll) {
      setScroll(firstItem);
    }
  }, [firstItem, isFocused, setScroll]);

  const onViewableItemsChanged = useCallback(
    (e: { viewableItems: ViewToken[]; changed: ViewToken[] }) => {
      setViewableItems(e.viewableItems);
    },
    []
  );

  if (props.interactive === false) {
    const sliceStart = props.scrollIndex
      ? props.size === "TWO_X_ONE"
        ? props.scrollIndex
        : props.scrollIndex - 1
      : 0;
    const sliceEnd = sliceStart + 5;

    return (
      <Box>
        {listData
          .map((item, index) => renderItem({ item, index }))
          .slice(sliceStart, sliceEnd)}
      </Box>
    );
  }

  return (
    <FlatList
      ref={listRef}
      data={listData}
      renderItem={renderItem}
      keyExtractor={(item) => item.id}
      getItemLayout={getItemLayout}
      onViewableItemsChanged={
        props.idiom === "TOUCH" ? onViewableItemsChanged : undefined
      }
      viewabilityConfig={props.idiom === "TOUCH" ? conf : undefined}
      showsVerticalScrollIndicator={Platform.OS !== "web"}
    />
  );
}
